import moment from "moment";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "../../../types/income";
import { HousingAssistanceSlice } from "../HousingAssistanceSlice";
import { Section } from "views/applications-id-report/components/Section";
import { HugeiconsIcon } from "@hugeicons/react";
import { getIncomeJobName, incomeTypeMap } from "../../../utils";
import { cx, has } from "shared/utils";
import { Badge, BadgeSize, BadgeVariant, Hide, Show } from "shared/components";
import { SummaryUpdated } from "../../../components/SummaryUpdated";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { ApplicationStatus, IncomeType, VerifyMethodType } from "shared/types";
import { TablesSlice } from "..";
import { formatMonths } from "../../../helpers";
import { IncomeSourceStatusBadge } from "../../../components/IncomeSourceStatusBadge";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Tabs } from "shared/components/Tabs";
import {
  InformationCircleIcon,
  Loading03Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { IncomeSliceHeader } from "./components/Header";
import { SelfReportedStats } from "./components/SelfReportedStats";
import { VerifiedReportedStats } from "./components/VerifiedReportedStats";
import { VerifiedHeader } from "./components/VerifiedHeader";
import { OfferLetterAssets } from "./components/OfferLetterAssets";
import { VerifiedItem } from "../../../types/verified";
import { WarningRecommendation } from "../components/WarningRecommendation";
import { BankStatementItem } from "../../../types/verified/bank-statment";
import { VerificationSettings } from "types/landlord/reports";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";

export const IncomeSlice = ({
  income,
  applicationStatus,
  applicationId,
  additionalData,
  settings,
  setShowReverifyIncomeModal,
  setShowEditIncomeModal,
  isManualApplicant
}: {
  income: CombinedIncome;
  applicationStatus: ApplicationStatus;
  applicationId?: string | number;
  additionalData?: VerificationSpecificAdditionaData | null;
  settings?: VerificationSettings;
  setShowReverifyIncomeModal: Dispatch<SetStateAction<number | false>>;
  setShowEditIncomeModal: Dispatch<
    SetStateAction<
      | false
      | {
          grossMonthlyIncomeCents: number;
          netMonthlyIncomeCents: number;
          hasNetMonthlyIncome: boolean;
          incomeId?: number;
          isTotal: boolean;
        }
    >
  >;
  isManualApplicant: boolean;
}) => {
  const [currentIncomeIndex, setCurrentIncomeIndex] = useState(0);

  const diff = moment().diff(moment(income.start_date), "months");

  const { groupedIncomeItems } = useMemo(() => {
    const incomeItems = [...income.items].reverse();

    const groupedItems = incomeItems.reduce(
      (acc, item) => {
        if (item.income_source_type === VerifyMethodType.BankStatement) {
          const groupedBankStatement = acc.find((incomeItem) =>
            Array.isArray(incomeItem)
          );
          if (groupedBankStatement) groupedBankStatement.push(item);
          else acc.push([item]);

          return acc;
        }

        acc.push(item);
        return acc;
      },
      [] as (VerifiedItem | BankStatementItem[])[]
    );

    return {
      incomeItems,
      groupedIncomeItems: groupedItems
    };
  }, [income.items, income.type, settings]);

  const currentItem = groupedIncomeItems[currentIncomeIndex] as
      | VerifiedItem
      | VerifiedItem[]
      | undefined,
    currentItemReverifyLog = Array.isArray(currentItem)
      ? currentItem[0]?.reverify_log
      : currentItem?.reverify_log;

  if (income.type === "housing_assistance")
    return (
      <HousingAssistanceSlice
        amountCents={income.amount_cents}
        assets={income.assets}
        incomeId={income.id}
      />
    );

  const tabsDisplayMap = groupedIncomeItems.reduce((acc, i, idx) => {
    const isLast = idx === groupedIncomeItems.length - 1,
      reverificationCount = groupedIncomeItems.length - idx - 1,
      isArchived = Array.isArray(i)
        ? i.every((item) => item.is_archived)
        : i.is_archived;

    acc[String(idx)] = (
      <p className="text-sm">
        {isLast
          ? "Original verification "
          : `Re-verification ${reverificationCount} `}
        <Badge
          size={BadgeSize.SM}
          className="ml-1"
          variant={isArchived ? BadgeVariant.BASE : BadgeVariant.SUCCESS}
        >
          {isArchived ? "Archived" : "Active"}
        </Badge>
      </p>
    );

    return acc;
  }, {});

  return (
    <Section
      id={`combined-income-section-${income.id}`}
      maxWidth="1000px"
      sectionTitle={
        <p className="flex items-center gap-2 text-content-400 font-medium text-sm">
          {income.additional_data ? (
            <>
              {getIncomeJobName(income)}
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
            </>
          ) : (
            ""
          )}
          {incomeTypeMap[income.type] || income.type}
          {income.status === "processing" ? null : (
            <>
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
              <IncomeSourceStatusBadge income={income} />
            </>
          )}
        </p>
      }
      rightElement={
        <p className="text-content-400 text-sm font-medium">
          {income.type === "offer_letter"
            ? moment(income.start_date).format("ll")
            : `${moment(income.start_date).format("ll")} - Present (${formatMonths(diff)})`}
        </p>
      }
    >
      <IncomeSliceHeader
        income={income}
        currentItem={currentItem}
        setShowEditIncomeModal={setShowEditIncomeModal}
      />
      <Show
        if={
          has(income.additional_data?.overrides?.gross_monthly_income) ||
          has(income.additional_data?.overrides?.net_monthly_income)
        }
      >
        <SummaryUpdated
          className="mb-10"
          changes={income.additional_data?.overrides}
          original={{
            gross: (income.amount_cents || 0) / 100,
            net: income.verified_monthly_amounts.net
          }}
        />
      </Show>

      <SelfReportedStats income={income} />

      {income.status === "processing" ? (
        <div className="flex flex-col items-center justify-center min-h-[250px] max-w-[468px] w-full mx-auto">
          <div className="size-16 shrink-0 bg-warning-50 rounded-full flex items-center justify-center mb-6 mx-auto">
            <HugeiconsIcon
              icon={Loading03Icon}
              size={24}
              className="text-warning-600"
            />
          </div>

          <h2 className="text-base font-bold text-content-500 mb-2 text-center">
            Verifying income source with documents...
          </h2>

          <p className="text-center text-sm font-medium text-content-300">
            Verification may take up to an hour
          </p>
        </div>
      ) : (
        <>
          {groupedIncomeItems.length > 1 ? (
            <>
              <Tabs
                variant="underline"
                tabs={groupedIncomeItems.map((_i, idx) => String(idx))}
                displayMap={tabsDisplayMap}
                setActiveTab={(t) => setCurrentIncomeIndex(Number(t))}
                selected={String(currentIncomeIndex)}
              />
              <OfferLetterAssets income={income} />
            </>
          ) : (
            <ContentTitle
              title={
                income.type === IncomeType.OfferLetter
                  ? "Offer letter"
                  : "Verification method"
              }
            >
              <Show if={income.type === IncomeType.OfferLetter}>
                <WarningRecommendation
                  status="Rejected"
                  type={income.type}
                  rejectionReason={
                    "assets" in income && !income.assets?.length
                      ? "No file was uploaded"
                      : ""
                  }
                  className="mb-4"
                />
              </Show>
              <OfferLetterAssets income={income} />
            </ContentTitle>
          )}

          {Object.keys(currentItemReverifyLog || {}).length ? (
            <div className="flex gap-4 items-center mb-2 mt-4 animate-appear">
              <HugeiconsIcon
                icon={InformationCircleIcon}
                className="text-content-200 shrink-0"
                size={20}
              />

              <p className="text-sm font-medium text-content-400">
                Re-verification was initiated by{" "}
                <span className="font-bold text-content-1000">
                  {currentItemReverifyLog?.author.name}
                </span>{" "}
                on{" "}
                <span className="font-bold text-content-1000">
                  {moment(currentItemReverifyLog?.created_at).format(
                    "ll [at] h:mm A"
                  )}
                </span>
              </p>
            </div>
          ) : null}
          <Hide if={income.type === IncomeType.OfferLetter}>
            <div
              key={currentIncomeIndex}
              className={cx(
                "pb-4 animate-appear",
                groupedIncomeItems.length > 1 ? "pt-4" : ""
              )}
            >
              <VerifiedHeader
                applicationId={applicationId || 0}
                applicationStatus={applicationStatus}
                currentItem={currentItem}
                income={income}
                setShowReverifyIncomeModal={setShowReverifyIncomeModal}
                isManualApplicant={isManualApplicant}
              />

              <VerifiedReportedStats currentItem={currentItem} />

              {currentItem ? (
                <TablesSlice
                  currentItem={currentItem}
                  additionalData={additionalData}
                />
              ) : (
                <>
                  <p className="text-base text-content-500">
                    No verified income sources
                  </p>
                  <div className="h-[1px] w-full bg-alpha-40 my-3" />
                  <div className="flex flex-wrap space-x-4">
                    {income.assets?.map(({ asset, global_id }) => (
                      <AssetItem
                        key={global_id}
                        url={asset?.url || ""}
                        content_type={asset?.content_type || ""}
                        filename={asset?.filename || ""}
                        assetName={asset?.filename || ""}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </Hide>
        </>
      )}
    </Section>
  );
};
